/**
 * Avatar personalizable: el busto de cada jugador (cabeza, hombros y pecho).
 *
 * Se guarda en la misma columna `users.avatar` que los emblemas de emoji,
 * codificado en una cadena corta: `av1.` seguido de un valor por campo,
 * separados por puntos. Los estilos van en base 36 y los colores en hex de
 * seis cifras. Así todas las pantallas que ya reciben `avatar` reciben
 * también el muñeco sin tocar ninguna consulta.
 */

export const AVATAR_PREFIX = 'av1.';

/** Campos de estilo: índice dentro de la lista de opciones de cada uno. */
export const STYLE_FIELDS = [
  'body',
  'hair',
  'brows',
  'eyes',
  'nose',
  'mouth',
  'marks',
  'beard',
  'top',
  'hat',
  'glasses',
  'earrings',
  'neck',
  'bg',
] as const;

/** Campos de color, en hex sin almohadilla. */
export const COLOR_FIELDS = [
  'skin',
  'hairColor',
  'eyeColor',
  'beardColor',
  'topColor',
  'topColor2',
  'hatColor',
  'glassesColor',
  'accColor',
  'bgColor',
] as const;

export type StyleField = (typeof STYLE_FIELDS)[number];
export type ColorField = (typeof COLOR_FIELDS)[number];
export type AvatarLook = Record<StyleField, number> & Record<ColorField, string>;

/** Opciones de cada campo de estilo. El orden es parte del formato: solo se añade al final. */
export const AVATAR_OPTIONS: Record<StyleField, string[]> = {
  body: ['Hombre', 'Mujer'],
  hair: [
    'Calvo',
    'Rapado',
    'Corto',
    'Despeinado',
    'Raya al lado',
    'Tupé',
    'Afro',
    'Rizos cortos',
    'Cresta',
    'Moño',
    'Coleta',
    'Largo liso',
    'Largo ondulado',
    'Media melena',
    'Trenzas',
    'Flequillo',
    'Dos coletas',
    'Rastas',
    'Hacia atrás',
    'Entradas',
    'Pixie',
    'Bob',
    'Rizos largos',
    'Melena lateral',
    'Degradado',
  ],
  brows: ['Normales', 'Gruesas', 'Finas', 'Arqueadas', 'Enfadadas', 'Preocupadas'],
  eyes: ['Normales', 'Abiertos', 'Sonrientes', 'Párpado caído', 'Guiño', 'Delineados', 'Serios', 'Estrellas'],
  nose: ['Recta', 'Pequeña', 'Grande', 'Aguileña'],
  mouth: ['Sonrisa', 'Risa', 'Neutra', 'Sorpresa', 'Lengua', 'Seria', 'Labios pintados', 'Media sonrisa'],
  marks: ['Nada', 'Rubor', 'Pecas', 'Lunar', 'Cicatriz', 'Maquillaje'],
  beard: ['Nada', 'Tres días', 'Bigote', 'Perilla', 'Barba', 'Barba larga', 'Bigote imperial', 'Barba corta', 'Candado', 'Patillas'],
  top: [
    'Camiseta',
    'Tirantes',
    'Camisa',
    'Sudadera',
    'Jersey',
    'Chaqueta',
    'Traje',
    'Vestido',
    'Rayas',
    'Camiseta del caballo',
    'Polo',
    'Jersey de cuadros',
    'Cuello alto',
    'Jersey de pico',
    'Camisa de cuadros',
    'Americana y corbata',
    'Chaleco',
    'Blusa',
    'Cazadora vaquera',
    'Abrigo',
    'Escote barco',
    'Top cruzado',
  ],
  hat: [
    'Nada',
    'Gorra',
    'Gorra al revés',
    'Gorro de lana',
    'Chistera',
    'Corona',
    'Vaquero',
    'Boina',
    'Diadema',
    'Lazo',
    'Auriculares',
    'Mago',
    'Fiesta',
    'Pañuelo',
    'Gorro de pescador',
    'Fedora',
  ],
  glasses: ['Nada', 'Redondas', 'Cuadradas', 'De sol', 'Aviador', 'Monóculo', 'Corazones', 'Media montura', 'Ojo de gato'],
  earrings: ['Nada', 'Botones', 'Aros', 'Colgantes', 'Aros grandes'],
  neck: ['Nada', 'Collar', 'Bufanda', 'Pajarita', 'Corbata', 'Medalla', 'Perlas', 'Pañuelo al cuello', 'Cadena'],
  bg: ['Sin fondo', 'Círculo', 'Cuadrado', 'Degradado', 'Tablero'],
};

/**
 * Opciones de la primera versión, de aire infantil, que ya no se ofrecen.
 * Siguen en `AVATAR_OPTIONS` porque el índice es parte del formato; quien
 * las tuviera guardadas ve en su lugar la opción por defecto del campo.
 */
export const RETIRED_OPTIONS: Partial<Record<StyleField, readonly number[]>> = {
  eyes: [7], // Estrellas
  mouth: [4], // Lengua
  glasses: [6], // Corazones
  hat: [5, 11, 12], // Corona, Mago, Fiesta
};

export function isRetiredOption(field: StyleField, index: number): boolean {
  return RETIRED_OPTIONS[field]?.includes(index) ?? false;
}

/** El look tal y como se dibuja: sin opciones retiradas. */
export function normalizeLook(look: AvatarLook): AvatarLook {
  let result = look;
  for (const field of Object.keys(RETIRED_OPTIONS) as StyleField[]) {
    if (isRetiredOption(field, look[field])) result = { ...result, [field]: 0 };
  }
  return result;
}

export const DEFAULT_LOOK: AvatarLook = {
  body: 0,
  hair: 5,
  brows: 0,
  eyes: 0,
  nose: 0,
  mouth: 0,
  marks: 0,
  beard: 0,
  top: 0,
  hat: 0,
  glasses: 0,
  earrings: 0,
  neck: 0,
  bg: 1,
  skin: 'e0ac85',
  hairColor: '3b2417',
  eyeColor: '4a2f1b',
  beardColor: '3b2417',
  topColor: '1f7fb3',
  topColor2: 'f4f1ea',
  hatColor: 'c04a45',
  glassesColor: '222222',
  accColor: 'd0a340',
  bgColor: 'b9cbe0',
};

/** Un punto de partida razonable para cada cuerpo. */
export const DEFAULT_LOOK_FEMALE: AvatarLook = {
  ...DEFAULT_LOOK,
  body: 1,
  hair: 12,
  eyes: 5,
  mouth: 0,
  top: 17,
  topColor: '8e2b2b',
  earrings: 1,
  bgColor: 'e6d3cf',
};

const HEX = /^[0-9a-f]{6}$/;

export function isAvatarCode(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.startsWith(AVATAR_PREFIX);
}

export function encodeAvatar(look: AvatarLook): string {
  const styles = STYLE_FIELDS.map((field) => look[field].toString(36));
  const colors = COLOR_FIELDS.map((field) => look[field].toLowerCase());
  return AVATAR_PREFIX + [...styles, ...colors].join('.');
}

/**
 * Lee un código de avatar. Es tolerante: un campo que falte o no se entienda
 * toma el valor por defecto, así que los códigos antiguos siguen dibujándose
 * si algún día se añaden campos.
 */
export function decodeAvatar(code: string): AvatarLook {
  const parts = code.slice(AVATAR_PREFIX.length).split('.');
  const look = { ...DEFAULT_LOOK };
  STYLE_FIELDS.forEach((field, index) => {
    const value = Number.parseInt(parts[index] ?? '', 36);
    if (Number.isInteger(value) && value >= 0 && value < AVATAR_OPTIONS[field].length) {
      look[field] = value;
    }
  });
  COLOR_FIELDS.forEach((field, index) => {
    const value = (parts[STYLE_FIELDS.length + index] ?? '').toLowerCase();
    if (HEX.test(value)) look[field] = value;
  });
  return look;
}

/** Validación estricta para el servidor: exactamente lo que genera `encodeAvatar`. */
export function isValidAvatarCode(value: string): boolean {
  if (!isAvatarCode(value) || value.length > 200) return false;
  const parts = value.slice(AVATAR_PREFIX.length).split('.');
  if (parts.length !== STYLE_FIELDS.length + COLOR_FIELDS.length) return false;
  const stylesOk = STYLE_FIELDS.every((field, index) => {
    const part = parts[index]!;
    const value = Number.parseInt(part, 36);
    return /^[0-9a-z]{1,2}$/.test(part) && value < AVATAR_OPTIONS[field].length;
  });
  return stylesOk && COLOR_FIELDS.every((_, index) => HEX.test(parts[STYLE_FIELDS.length + index]!));
}

/* ------------------------------------------------------------------ */
/* Hombre y mujer                                                      */
/* ------------------------------------------------------------------ */

/** Peinados que encajan con cada cuerpo (los que están en las dos listas, con ambos). */
export const HAIR_BY_BODY: readonly [readonly number[], readonly number[]] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 17, 18, 19, 24],
  [6, 7, 9, 10, 11, 12, 13, 14, 15, 16, 17, 20, 21, 22, 23],
];

/** Prendas que solo se ofrecen al cuerpo de mujer al pasar de uno a otro. */
const FEMALE_TOPS = [7, 17, 20, 21];

/**
 * Cambia de cuerpo arrastrando lo que lo delata: el peinado, la barba, las
 * pestañas, los pendientes y el maquillaje. La ropa, los colores y los
 * complementos neutros se quedan como estaban.
 */
export function switchBody(look: AvatarLook, body: number): AvatarLook {
  if (look.body === body) return look;
  const next: AvatarLook = { ...look, body };
  const fits = HAIR_BY_BODY[body === 1 ? 1 : 0]!;
  if (body === 1) {
    if (!fits.includes(look.hair)) next.hair = look.hair === 7 ? 22 : look.hair === 3 ? 20 : 12;
    next.beard = 0;
    if (look.eyes === 0) next.eyes = 5;
    if (look.earrings === 0) next.earrings = 1;
    if (look.brows === 1) next.brows = 3;
  } else {
    if (!fits.includes(look.hair)) next.hair = look.hair === 22 ? 7 : look.hair === 20 ? 3 : 5;
    if (look.eyes === 5) next.eyes = 0;
    if (look.mouth === 6) next.mouth = 0;
    if (look.marks === 5) next.marks = 0;
    next.earrings = 0;
    if (FEMALE_TOPS.includes(look.top)) next.top = 0;
  }
  return next;
}

/** Un muñeco al azar, para el botón «Sorpréndeme». */
export function randomLook(random: () => number = Math.random): AvatarLook {
  const pick = <T,>(list: readonly T[]) => list[Math.floor(random() * list.length)]!;
  const index = (field: StyleField) => Math.floor(random() * AVATAR_OPTIONS[field].length);
  const female = random() < 0.5;
  const look = { ...DEFAULT_LOOK } as AvatarLook;
  for (const field of STYLE_FIELDS) {
    do look[field] = index(field);
    while (isRetiredOption(field, look[field]));
  }
  look.body = female ? 1 : 0;
  look.hair = pick(HAIR_BY_BODY[female ? 1 : 0]!);
  if (!female && FEMALE_TOPS.includes(look.top)) look.top = 0;
  if (!female && look.mouth === 6) look.mouth = 0;
  // Sin excesos: menos barbas en mujeres, menos accesorios a la vez.
  if (female || random() < 0.5) look.beard = 0;
  if (random() < 0.5) look.hat = 0;
  if (random() < 0.6) look.glasses = 0;
  if (random() < 0.5) look.neck = 0;
  if (!female && random() < 0.7) look.earrings = 0;
  if (look.bg === 0) look.bg = 1;
  look.skin = pick(SKIN_TONES);
  look.hairColor = pick(HAIR_COLORS);
  look.beardColor = look.hairColor;
  look.eyeColor = pick(EYE_COLORS);
  look.topColor = pick(CLOTH_COLORS);
  look.topColor2 = pick(CLOTH_COLORS);
  look.hatColor = pick(CLOTH_COLORS);
  look.glassesColor = pick(['222222', '8a5a2b', 'c04a45', '2a6bb4', 'd0a340']);
  look.accColor = pick(['d0a340', 'c9ccd1', 'c04a45', '2a6bb4', 'f4f1ea']);
  look.bgColor = pick(BG_COLORS);
  return look;
}

export const SKIN_TONES = [
  'ffe0c7',
  'f9d3b4',
  'f1c27d',
  'e0ac85',
  'd19a6a',
  'c68642',
  'a86b3c',
  '8d5524',
  '6b3e1f',
  '4a2912',
];

export const HAIR_COLORS = [
  '0f0f10',
  '3b2417',
  '6a4027',
  '8d5a33',
  'b07a45',
  'd8b26e',
  'f0da9c',
  'a33a1f',
  'd7672a',
  'b9b9b9',
  'f4f4f4',
  '2a6bb4',
  'c75fa8',
  '4caf6a',
  '7a4fc4',
  'e84a5f',
];

export const EYE_COLORS = ['4a2f1b', '7a4a24', '2d6fa3', '6a9ec9', '3f7d4a', '8a9a5b', '6d6d6d', '1a1a1a', '9b59b6'];

export const CLOTH_COLORS = [
  'ffffff',
  'f4f1ea',
  'c9ccd1',
  '6e7380',
  '2b2b2b',
  '111111',
  '2a6bb4',
  '1b4a7c',
  '3fb3c7',
  '4b8a1f',
  '7db34d',
  'd0a340',
  'f2d24b',
  'd98c3a',
  'c04a45',
  '8e2b2b',
  'e98bb0',
  'b565c7',
  '5b3f9c',
  '7a5236',
  'c8a27a',
  '2f3f5c',
];

/** Fondos apagados: el protagonista es el retrato, no el color. */
export const BG_COLORS = [
  'b9cbe0',
  '2a6bb4',
  'c7d6c4',
  'e6d3cf',
  'e8dcc0',
  'd6d0e2',
  'e3cdb8',
  'c9ccd1',
  '272a31',
  'f4f1ea',
];

/* ------------------------------------------------------------------ */
/* Foto de perfil                                                      */
/* ------------------------------------------------------------------ */

/**
 * Una foto subida por el jugador. Va en la misma columna `avatar`, como
 * `ph1.` seguido del nombre del archivo, así que llega a todas las pantallas
 * igual que el busto. El archivo cambia de nombre con cada foto nueva y se
 * puede cachear para siempre.
 */
export const PHOTO_PREFIX = 'ph1.';

const PHOTO_FILE = /^[a-z0-9]{6,40}\.(jpg|webp)$/;

export function isPhotoFile(file: string): boolean {
  return PHOTO_FILE.test(file);
}

export function isPhotoAvatar(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.startsWith(PHOTO_PREFIX) && isPhotoFile(value.slice(PHOTO_PREFIX.length));
}

/** Dónde se sirve la foto. */
export function photoUrl(value: string): string {
  return `/api/avatars/${value.slice(PHOTO_PREFIX.length)}`;
}

/** El avatar es una imagen que llena su hueco (busto o foto), no un emoji. */
export function isPictureAvatar(value: string | null | undefined): value is string {
  return isAvatarCode(value) || isPhotoAvatar(value);
}
